import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import refPropType from 'utils/propTypes/ref';

import NumberInput from './NumberInput';
import AutoGrowTextArea from './AutoGrowTextArea';

import style from './Input.module.scss';

/**
 * Input mimicks the native HTML `input` element, providing compsable components styled for our purpose.
 *
 * It will pass all extra properties to the underlying `input` element, so you can add `onChange` etc. handlers straight to the component.
 */
export default function Input({
  className,
  type,
  inputRef,
  autoGrow,
  ...otherProps
}) {
  const InputComponent = {
    text: 'input',
    password: 'input',
    textarea: autoGrow ? AutoGrowTextArea : 'textarea',
    number: NumberInput,
  }[type];

  return (
    <InputComponent
      className={classnames(style.input, className)}
      type={type}
      // NumberInput and AutoGrowTextArea handle inputRef => ref internally:
      {...(type === 'number' || (type === 'textarea' && autoGrow)
        ? { inputRef }
        : { ref: inputRef })}
      {...otherProps}
    />
  );
}

Input.displayName = 'Input';
Input.propTypes = {
  /**
   * when rendering a textarea, this bool determines whether
   * the input will vertically expand/contract automatically
   * as input text wraps to a new line/is deleted
   */
  autoGrow: PropTypes.bool,
  /**
   * class name to pass into input
   */
  className: PropTypes.string,
  /**
   * an html `input` type attribute
   */
  type: PropTypes.oneOf(['text', 'textarea', 'number', 'password']),
  /**
   * reference to underlying `input` dom element
   */
  inputRef: refPropType,
};

Input.defaultProps = {
  autoGrow: false,
  className: '',
  type: 'text',
  inputRef: null,
};
